// src/pages/sow-template/sowTemplateHelpers.js
//
// Pure helpers for the SOW Template builder -- placeholder extraction,
// key/number parsing, and the preview HTML renderer. Split out of
// SowTemplatePage.jsx; none of this depends on state or the API.

export const CONTRACT_TYPE_OPTIONS = [
  { value: "", label: "All contract types" },
  { value: "T&M", label: "T&M" },
  { value: "Fixed Bid", label: "Fixed Bid" },
  { value: "Managed Services", label: "Managed Services" },
];

// Support both {{placeholder}} and <Placeholder> formats
const PLACEHOLDER_REGEX = /(?:\{\{\s*([a-zA-Z0-9_]+)\s*\}\})|(?:<([A-Za-z][A-Za-z0-9_ ]*[A-Za-z0-9])>)/g;

export function extractPlaceholdersFromText(txt) {
  const out = new Set();
  const s = String(txt || "");
  let m;
  const regex = new RegExp(PLACEHOLDER_REGEX.source, "g");
  while ((m = regex.exec(s)) !== null) {
    const ph = m[1] || m[2];
    if (ph) {
      const normalized = ph.replace(/\s+/g, "_").toLowerCase();
      out.add(normalized);
    }
  }
  return Array.from(out);
}

export function safeKeyFromTitle(title) {
  return (
    String(title || "")
      .trim()
      .toLowerCase()
      .replace(/^[\d.]+\s*/, "")
      .replace(/[^a-z0-9_ ]/g, "")
      .replace(/\s+/g, "_")
      .slice(0, 50) || "section"
  );
}

export function extractSectionNumber(title) {
  const match = String(title || "").match(/^(\d+)\.?\s*/);
  return match ? match[1] : null;
}

/** Renders the same preview markup SowTemplatePage always used (exec-summary
 * table + table of contents + rendered sections), just relocated. */
export function renderPreviewHTML(tpl) {
  if (!tpl) return "<div class='text-sm text-muted'>No template selected.</div>";

  const esc = (s) =>
    String(s || "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;");

  const renderText = (txt) => {
    let html = esc(txt);
    html = html.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_m, k) => {
      return `<span class="sow-tpl-ph" title="Placeholder: ${esc(k)}">{{${esc(k)}}}</span>`;
    });
    html = html.replace(/&lt;([A-Za-z][A-Za-z0-9_ ]*[A-Za-z0-9])&gt;/g, (_m, k) => {
      return `<span class="sow-tpl-ph" title="Placeholder: ${k}">&lt;${k}&gt;</span>`;
    });
    return html.replaceAll("\n", "<br/>");
  };

  const parts = [];

  parts.push(`
    <div class="sow-doc">
      <div class="sow-header">
        <div class="sow-title-block">
          <div class="sow-project-name">${renderText("<Project Name>")}</div>
          <div class="sow-for">for ${renderText("<Client Name>")}</div>
          <div class="sow-subtitle">Statement of Work for Professional Services</div>
        </div>
      </div>
  `);

  parts.push(`
    <table class="sow-exec-table">
      <tr><td colspan="4" class="sow-exec-header">Executive Summary</td></tr>
      <tr><td class="sow-label">Customer</td><td colspan="3">${renderText("<Client Name>")}</td></tr>
      <tr>
        <td class="sow-label">Scope of Services</td>
        <td colspan="3">This Statement of Work ("SOW") outlines the professional services to be performed by Jade Global for ${renderText(
          "<Client Name>"
        )}.</td>
      </tr>
      <tr>
        <td class="sow-label">Schedule</td>
        <td><strong>Start:</strong> ${renderText("<Start Date>")}</td>
        <td><strong>End:</strong> ${renderText("<End Date>")}</td>
        <td></td>
      </tr>
      <tr>
        <td class="sow-label">Customer Contact</td>
        <td>${renderText("<Client Contact Name>")}<br/>${renderText("<Client Contact Email>")}</td>
        <td class="sow-label">Jade Contact</td>
        <td>${renderText("<Jade Contact Name>")}<br/>${renderText("<Jade Contact Email>")}</td>
      </tr>
    </table>
  `);

  const sections = (tpl.sections_json || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));

  parts.push(`<div class="sow-toc"><div class="sow-toc-title">TABLE OF CONTENTS</div>`);
  sections.forEach((s, idx) => {
    const num = extractSectionNumber(s.title) || idx + 1;
    const title = s.title.replace(/^\d+\.?\s*/, "");
    parts.push(`<div class="sow-toc-item"><span class="sow-toc-num">${num}.</span> ${esc(title)}</div>`);
  });
  parts.push(`</div>`);

  for (const s of sections) {
    const num = extractSectionNumber(s.title);
    const titleText = s.title.replace(/^\d+\.?\s*/, "");
    parts.push(`
      <div class="sow-section">
        <div class="sow-section-title">${num ? `<span class="sow-section-num">${num}.</span>` : ""} ${esc(
      titleText
    )}</div>
        <div class="sow-section-content">${
          renderText(s.content_template || "") || "<span class='text-muted'>(No content)</span>"
        }</div>
      </div>
    `);
  }

  parts.push(`</div>`);
  return parts.join("");
}
